import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import TacticsList from '../components/tactics/TacticsList';
import TacticsModal from '../components/tactics/TacticsModal';
import { tacticsService } from '../services/tacticsService';
import { toast } from 'react-toastify';
import Spinner from '../components/common/Spinner';

const SAMPLE_TACTICS = [
  {
    id: 1,
    tactic_name: 'Daily Vitamin D3 (2000 IU) & K2 Supplementation',
    tactic_category: 'Supplements',
    impacted_biomarkers: ['Vitamin D', 'Calcium', 'Bone Density'],
    impacted_goals: ['Increase Healthspan', 'Bone Health'],
    how_to_do_it: 'Take 2000 IU of Vitamin D3 and 100mcg of K2 daily with a meal containing fat for better absorption. Consistency is key for optimal results.',
    references: '<a href="https://pubmed.ncbi.nlm.nih.gov/">Vitamin D and K2 Synergy Study</a>'
  },
  {
    id: 2,
    tactic_name: 'Limit Saturated Fat Intake',
    tactic_category: 'Diet & Nutrition',
    impacted_biomarkers: ['Cholesterol', 'LDL', 'Triglycerides'],
    impacted_goals: ['Cardiovascular Health', 'Weight loss'],
    how_to_do_it: 'Reduce saturated fat intake to less than 10% of daily calories. Replace with unsaturated fats from nuts, seeds, and olive oil. Aim for 2-3 servings of fatty fish per week.',
    references: '<a href="https://pubmed.ncbi.nlm.nih.gov/">Saturated Fat and Heart Health</a>'
  },
  {
    id: 3,
    tactic_name: 'High-Intensity Interval Training (HIIT)',
    tactic_category: 'Exercise',
    impacted_biomarkers: ['Heart Rate', 'VO2 Max', 'Blood Glucose', 'Muscle Mass'],
    impacted_goals: ['Improve Energy', 'Cardiovascular Health', 'Increase Healthspan'],
    how_to_do_it: 'Perform 20-30 minutes of HIIT 2-3 times per week. Alternate between 30 seconds of high-intensity effort and 30 seconds of recovery. Examples: sprinting, cycling, rowing.',
    references: '<a href="https://pubmed.ncbi.nlm.nih.gov/">HIIT Benefits Research</a>'
  }
];

const TacticsMasterPage = () => {
  const [tactics, setTactics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTactic, setSelectedTactic] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  // Fetch all tactics
  const fetchTactics = async () => {
    try {
      setLoading(true);
      const response = await tacticsService.getAllTactics();
      setTactics(response.data || []);
    } catch (error) {
      toast.error('Failed to load tactics');
      console.error('Error fetching tactics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTactics();
  }, []);

  const handleCreateTactic = () => {
    setSelectedTactic(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditTactic = (tactic) => {
    setSelectedTactic(tactic);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteTactic = async (tacticId) => {
    if (window.confirm('Are you sure you want to delete this tactic?')) {
      try {
        await tacticsService.deleteTactic(tacticId);
        toast.success('Tactic deleted successfully');
        fetchTactics();
      } catch (error) {
        toast.error('Failed to delete tactic');
        console.error('Error deleting tactic:', error);
      }
    }
  };

  const handleSaveTactic = async (tacticData) => {
    try {
      if (modalMode === 'create') {
        await tacticsService.createTactic(tacticData);
        toast.success('Tactic created successfully');
      } else {
        await tacticsService.updateTactic(selectedTactic.id, tacticData);
        toast.success('Tactic updated successfully');
      }
      setShowModal(false);
      fetchTactics();
    } catch (error) {
      toast.error(modalMode === 'create' ? 'Failed to create tactic' : 'Failed to update tactic');
      console.error('Error saving tactic:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Tactics Master</h1>
          <button
            onClick={handleCreateTactic}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            + Add New Tactic
          </button>
        </div>

        <TacticsList
          tactics={tactics}
          onEdit={handleEditTactic}
          onDelete={handleDeleteTactic}
        />
      </div>

      {showModal && (
        <TacticsModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSaveTactic}
          tactic={selectedTactic}
          mode={modalMode}
        />
      )}
    </Layout>
  );
};

export default TacticsMasterPage;
